import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import crmService from '@/services/crmService'
import { convertToCalendarHolidays, generateDeliveryDates } from '@/utils/deliverySchedule'
import DeliverySchedule from '@/components/sun/DeliverySchedule'
import { EMPTY_OVERLAY } from '@/utils/deliveryOverlay'
import type { Subscription, HolidayRequest, LineAmendment, SubscriptionLine, DeliveryOverlay } from '@/types'

import '@/styles/pages/crm-deliveries.css'

/**
 * The staff session for this page load, kept in memory only: cookies are
 * blocked in a cross-site iframe, and storage would outlive the sign-in.
 */
let staffSessionId: string | null = null

/** The sign-in in progress (or done) for the latest token, shared by every load. */
let signingIn: Promise<void> | null = null

/**
 * Take the Microsoft token from after the # (the form puts it there so it never
 * reaches a server log) and clear it from the address straight away, so it
 * isn't left in history or copied with a link.
 */
const takeTokenFromAddress = (): string | null => {
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token')
  if (window.location.hash) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search)
  }
  return token
}

/**
 * Make sure the latest token has been swapped for a session before loading.
 * Shared, so a second load that starts before the sign-in finishes (React runs
 * effects twice in development, and the token is wiped after the first) waits
 * for it rather than going ahead without a session.
 */
const ensureSignedIn = (): Promise<void> => {
  const token = takeTokenFromAddress()
  if (token) {
    signingIn = crmService.startSession(token).then(session => { staffSessionId = session.sessionId })
  }
  return signingIn ?? Promise.resolve()
}

/** What to tell staff when the view can't be shown. */
const messageFor = (err: { status?: number, message?: string }): string => {
  switch (err?.status) {
    case 401: return err.message || 'Your sign-in has expired. Please reopen the customer in Dynamics.'
    case 403: return err.message || "You don't have access to customer deliveries."
    case 404: return 'We could not find this customer.'
    default: return 'We could not load these deliveries just now.'
  }
}

/**
 * DF-345: the read-only delivery view embedded in Dynamics via an iframe.
 *
 * Dynamics opens it as /crm/deliveries?contact=<guid>&data_hash=<hash>#token=<token>.
 * The staff member's Microsoft token is swapped for a staff session (option 3),
 * which every deliveries request then carries. It reuses the customer-facing
 * delivery calendar, without the Edit / Pause controls, so staff and customers
 * see the same schedule.
 */
function CrmDeliveriesPage(): React.JSX.Element {
  const [params] = useSearchParams()
  const contact = params.get('contact')
  const dataHash = params.get('data_hash')

  const [deliveryDays, setDeliveryDays] = useState<number[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [holidays, setHolidays] = useState<HolidayRequest[]>([])
  const [amendments, setAmendments] = useState<LineAmendment[]>([])
  const [overlay, setOverlay] = useState<DeliveryOverlay>(EMPTY_OVERLAY)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Embedded in an iframe: paint the whole document the card's colour so no app
  // background can peek around the edges, and drop the app's page gutter (DF-345).
  useEffect(() => {
    document.documentElement.classList.add('crm-embed')
    return () => document.documentElement.classList.remove('crm-embed')
  }, [])

  const load = useCallback((mode: 'initial' | 'refresh') => {
    if (!contact) {
      setError('No customer was specified.')
      setLoading(false)
      return
    }

    if (mode === 'refresh') setRefreshing(true)
    else setLoading(true)
    setError(null)

    // A new token (Dynamics sends one each time a customer is opened) starts or
    // renews the staff session first.
    ensureSignedIn()
      .then(() => crmService.getDeliveries(contact, dataHash, staffSessionId))
      .then(data => {
        setDeliveryDays(data.customer?.delivery_days ?? [])
        setSubscriptions(data.subscriptions ?? [])
        setHolidays(data.holidays ?? [])
        setAmendments(data.amendments ?? [])
        setOverlay(data.overlay ?? EMPTY_OVERLAY)
      })
      .catch((err: { status?: number, message?: string }) => setError(messageFor(err)))
      .finally(() => { setLoading(false); setRefreshing(false) })
  }, [contact, dataHash])

  useEffect(() => { load('initial') }, [load])

  const calendarHolidays = useMemo(() => convertToCalendarHolidays(holidays), [holidays])

  const deliveries = useMemo(
    () => generateDeliveryDates(subscriptions, calendarHolidays, amendments, deliveryDays, 26, 400),
    [subscriptions, calendarHolidays, amendments, deliveryDays]
  )

  const oneOffLines = useMemo<SubscriptionLine[]>(
    () => subscriptions
      .flatMap(s => s.lines ?? [])
      .filter(l => l.is_one_off && !l.is_ended),
    [subscriptions]
  )

  if (error) {
    return (
      <div className="crm-deliveries">
        <p className="crm-deliveries-message">{error}</p>
      </div>
    )
  }

  return (
    <div className="crm-deliveries">
      <DeliverySchedule
        deliveries={deliveries}
        loading={loading}
        oneOffLines={oneOffLines}
        readOnly
        onRefresh={() => load('refresh')}
        refreshing={refreshing}
        roundAmendments={overlay.round_day_amendments}
        glassWindows={overlay.glass_windows}
        glassOptedOut={overlay.glass_opted_out}
        dateOverrides={overlay.date_overrides}
      />
    </div>
  )
}

export default CrmDeliveriesPage
